'use client'

import { useState } from 'react'
import { CheckCircle2, Landmark, Mail, MapPin, Send } from 'lucide-react'
import { PageHero } from '../../components/ui/kit'

/* ─────────────────────────────────────────────────────────────
   CONTACT (Phase 1 batch 3) — real submission via /api/contact
   (the old form faked success), honest channels only (the
   placeholder WhatsApp number is gone), Fable band hero.
───────────────────────────────────────────────────────────── */

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')

  function set(k: string, v: string) { setForm((p) => ({ ...p, [k]: v })) }

  async function submit() {
    if (!form.name || !form.message) { alert('Name and message are required'); return }
    setState('sending')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const d = await res.json().catch(() => null)
      setState(d?.ok ? 'sent' : 'failed')
    } catch {
      setState('failed')
    }
  }

  return (
    <main className="ctp">
      <PageHero
        compact
        motif="community"
        eyebrow="Contact"
        title={<>Get in touch.</>}
        subtitle="Questions about a listing, MasjidOS, waqf protection or partnerships — we read every message."
      />

      <div className="ctp-wrap ctp-body">
        {state === 'sent' ? (
          <div className="ctp-card ctp-done">
            <CheckCircle2 size={36} strokeWidth={1.5} />
            <h2>Message received</h2>
            <p>JazakAllahu khairan — your message is logged and the team will respond by email.</p>
          </div>
        ) : (
          <div className="ctp-card">
            <h2>Send us a message</h2>
            {state === 'failed' && (
              <div className="ctp-fail">
                The message couldn&rsquo;t be saved right now — please email us directly at{' '}
                <a href="mailto:support@masjids.in">support@masjids.in</a> and we&rsquo;ll pick it up there.
              </div>
            )}
            <div className="ctp-grid2">
              <label>Your name *<input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Mohammed Khan" /></label>
              <label>Email<input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" /></label>
            </div>
            <div className="ctp-grid2">
              <label>Phone<input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Optional" /></label>
              <label>Subject
                <select value={form.subject} onChange={(e) => set('subject', e.target.value)}>
                  <option value="">Select topic</option>
                  {['Register my masjid', 'Technical support', 'Billing & subscriptions', 'Waqf & legal help', 'Partnership', 'Other'].map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <label>Message *<textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={6} placeholder="Tell us how we can help…" /></label>
            <button type="button" onClick={submit} disabled={state === 'sending'}>
              <Send size={14} strokeWidth={1.5} /> {state === 'sending' ? 'Sending…' : 'Send message'}
            </button>
          </div>
        )}

        <aside className="ctp-rail">
          <div className="ctp-info">
            <span className="ctp-ic"><Mail size={17} strokeWidth={1.5} /></span>
            <div>
              <b>Email</b>
              <a href="mailto:support@masjids.in">support@masjids.in</a>
              <span>The fastest way to reach the team</span>
            </div>
          </div>
          <div className="ctp-info">
            <span className="ctp-ic"><MapPin size={17} strokeWidth={1.5} /></span>
            <div>
              <b>Based in</b>
              <em>Hyderabad, Telangana</em>
              <span>Serving masajid across India</span>
            </div>
          </div>
          <div className="ctp-committee">
            <Landmark size={16} strokeWidth={1.5} />
            <b>Run a masjid?</b>
            <p>Registration and claims have their own guided flows — no message needed.</p>
            <a href="/onboard">Register your masjid →</a>
            <a href="/masjid/claim">Claim an existing listing →</a>
          </div>
        </aside>
      </div>

      <style jsx>{`
        .ctp{background:var(--f-beige);}
        .ctp-wrap{max-width:980px;margin:0 auto;padding:0 26px}
        .ctp-hero{position:relative;z-index:0;padding:42px 0 34px}
        .ctp-hero-bg{position:absolute;left:0;right:0;top:0;bottom:-110px;z-index:-1;pointer-events:none;
          background:url('/hero-art/dome.svg') right -70px bottom -20px / 520px auto no-repeat,radial-gradient(560px 320px at 18% 30%, rgba(14,124,97,.14), transparent 62%),linear-gradient(170deg,#101A38,#0B132B);
          -webkit-mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%);
          mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%)}
        .ctp-pattern{position:absolute;inset:0;opacity:.04;background-image:radial-gradient(rgba(255,255,255,.85) 1px,transparent 1.5px);background-size:26px 26px}
        .ctp-eyebrow{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-mono);font-size:10px;letter-spacing:.18em;color:#14B8A6;border:1px solid rgba(20,184,166,.35);border-radius:99px;padding:4px 12px;background:rgba(20,184,166,.08);margin-bottom:12px}
        .ctp-hero h1{font-family:var(--f-display);font-size:clamp(24px,2.8vw,34px);font-weight:700;color:#F6F4EE;letter-spacing:-.01em}
        .ctp-hero p{color:rgba(246,244,238,.75);font-size:14px;margin-top:8px;max-width:560px}

        .ctp-body{display:grid;grid-template-columns:1fr 320px;gap:18px;padding-bottom:54px;align-items:start}
        .ctp-card{background:#fff;border:1px solid var(--f-line);border-radius:16px;padding:24px;box-shadow:var(--f-shadow)}
        .ctp-card h2{font-family:var(--f-display);font-size:16px;font-weight:600;color:var(--f-navy);margin-bottom:16px}
        .ctp-fail{background:rgba(212,90,80,.08);border:1px solid rgba(212,90,80,.25);border-radius:11px;padding:11px 14px;font-size:12.5px;color:#9C4038;margin-bottom:14px;line-height:1.55}
        .ctp-fail a{color:#9C4038;font-weight:600}
        .ctp-grid2{display:grid;grid-template-columns:1fr 1fr;gap:13px}
        label{display:block;font-size:12px;font-weight:600;color:var(--f-slate);margin-bottom:13px}
        input,select,textarea{display:block;width:100%;margin-top:6px;padding:10px 13px;border-radius:10px;border:1px solid var(--f-line-strong);font-size:13.5px;font-family:inherit;box-sizing:border-box;outline:none;background:#fff;color:var(--f-ink);transition:border-color .2s}
        input:focus,select:focus,textarea:focus{border-color:rgba(14,124,97,.45)}
        textarea{resize:vertical;line-height:1.65}
        button{display:inline-flex;align-items:center;gap:8px;background:var(--f-green);color:#fff;border:none;border-radius:11px;padding:12px 22px;font-size:13.5px;font-weight:600;cursor:pointer;font-family:inherit;transition:background .2s}
        button:hover{background:var(--f-green-deep)}
        button:disabled{opacity:.65;cursor:default}
        .ctp-done{text-align:center;padding:46px 24px;color:var(--f-green)}
        .ctp-done h2{margin:12px 0 6px}
        .ctp-done p{font-size:13.5px;color:var(--f-slate);line-height:1.6}

        .ctp-rail{display:grid;gap:13px}
        .ctp-info{display:flex;gap:13px;background:#fff;border:1px solid var(--f-line);border-radius:14px;padding:16px;box-shadow:var(--f-shadow)}
        .ctp-ic{width:38px;height:38px;border-radius:11px;background:rgba(14,124,97,.08);color:var(--f-green);display:grid;place-items:center;flex:none}
        .ctp-info b{display:block;font-size:13px;color:var(--f-navy);font-family:var(--f-display);font-weight:600}
        .ctp-info a,.ctp-info em{display:block;font-size:13px;font-weight:600;color:var(--f-green);text-decoration:none;font-style:normal;margin-top:2px}
        .ctp-info span{display:block;font-size:11px;color:var(--f-slate-dim);margin-top:2px}
        .ctp-committee{background:linear-gradient(150deg,#101A38,#0B132B);border-radius:14px;padding:19px;color:#fff}
        .ctp-committee b{display:block;font-family:var(--f-display);font-size:14px;margin-top:9px}
        .ctp-committee p{font-size:12px;color:rgba(246,244,238,.75);line-height:1.55;margin:6px 0 12px}
        .ctp-committee a{display:block;font-size:12.5px;font-weight:600;color:#2DD4BF;text-decoration:none;margin-top:7px}

        @media(max-width:760px){.ctp-body{grid-template-columns:1fr}.ctp-grid2{grid-template-columns:1fr}}
      `}</style>
    </main>
  )
}
